/**
 * Utilities for working with demo content
 * 
 * These functions help identify and process demo works that can be
 * accessed without authentication.
 */

import type { Work } from "@/app/types/book";
import { FEATURES } from "@/app/lib/config";

/**
 * Determines if a work qualifies as a publicly accessible demo
 * Demo works must:
 * 1. Have "demo" and "sample" tags (case-insensitive)
 * 2. Be marked as public (is_public === true)
 * 3. Feature flag must be enabled
 * 
 * @param work Work object to evaluate
 * @returns boolean indicating if the work is a public demo
 */
export function isDemoWork(work: Work | null | undefined): boolean {
  if (!FEATURES.PUBLIC_DEMO_WORKS) return false;
  
  if (!work) return false;
  
  // According to the spec, demo works must have BOTH "demo" AND "sample" tags
  // and be marked as public (is_public === true)
  const hasDemoTag = work.tags?.toLowerCase().includes("demo") || false;
  const hasSampleTag = work.tags?.toLowerCase().includes("sample") || false;
  const isPublic = work.is_public === true;
  
  // Spec requires BOTH tags AND is_public flag
  return isPublic && hasDemoTag && hasSampleTag;
}

/**
 * Removes sensitive data from a work object before serving it publicly
 * 
 * @param work Original work object
 * @returns Sanitized work object safe for public consumption
 */
export function sanitizeDemoWork(work: Work): Work {
  if (!work) return work;
  
  // Create a deep copy to avoid mutating the original
  const sanitized = JSON.parse(JSON.stringify(work));
  
  // Remove sensitive fields - only user_id is in the Work interface
  // We create a new object without the user_id to avoid delete operator on a required property
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user_id: _user_id, ...safeWork } = sanitized;
  
  // Fields that should be redacted/sanitized
  const sensitiveFields = [
    'payment',
    'billing',
    'private',
    'email',
    'phone',
    'address',
    'credit_card',
    'password',
    'api_key',
    'token',
    'secret'
  ];
  
  // Remove any payment, billing, or other sensitive information
  if (safeWork.metadata) {
    // Create a safe copy of metadata without sensitive information
    safeWork.metadata = { ...safeWork.metadata };
    
    // Recursively remove sensitive fields
    const removeSensitiveData = (obj: any) => {
      if (!obj || typeof obj !== 'object') return;
      
      // Remove all known sensitive fields
      sensitiveFields.forEach(field => {
        if (Object.prototype.hasOwnProperty.call(obj, field)) {
          delete obj[field];
        }
      });
      
      // Recursively check nested objects
      Object.keys(obj).forEach(key => {
        if (obj[key] && typeof obj[key] === 'object') {
          removeSensitiveData(obj[key]);
        }
      });
    };
    
    // Apply sanitization to all metadata
    removeSensitiveData(safeWork.metadata);
  }
  
  // Add demo information in metadata rather than a new property
  safeWork.metadata = {
    ...safeWork.metadata,
    is_demo: true,
    demo_access_timestamp: new Date().toISOString()
  };
  
  // Log sanitization activity
  console.log(`[demo-access] Work sanitized for public access: ${safeWork.friendly_id}, title: "${safeWork.title}"`);  
  
  return safeWork as Work;
}
